import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Trophy, Loader2, ArrowRightLeft, Flame, Target, Zap, CheckCircle2 } from "lucide-react";

/**
 * Multi-goal side-by-side comparison for the Analytics page.
 * - Ranks goals by velocity (tasks/day) and highlights the fastest one.
 * - Each row: dual progress bars for Readiness + Completion, plus streak & velocity.
 * - "Switch" button on non-active goals.
 */
export default function MultiGoalAnalytics() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/analytics/goals");
      setGoals(data.goals || []);
      setActiveId(data.active_goal_id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onSwitch = () => { setLoading(true); load(); };
    window.addEventListener("adyap:goal-switched", onSwitch);
    return () => window.removeEventListener("adyap:goal-switched", onSwitch);
  }, [load]);

  const activate = async (g) => {
    if (g.goal_id === activeId) return;
    setSwitching(g.goal_id);
    try {
      await api.post(`/goals/${g.goal_id}/activate`);
      await refreshUser();
      window.dispatchEvent(new Event("adyap:goal-switched"));
      toast.success(`Now tracking: ${g.role_target}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSwitching(null); }
  };

  if (loading) {
    return (
      <div className="glass rounded-2xl p-6 flex items-center gap-2 text-zinc-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading goals…
      </div>
    );
  }
  if (!goals.length) {
    return (
      <div className="glass rounded-2xl p-6 text-center">
        <Target className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
        <p className="text-sm text-zinc-400">No goals yet. Create your first goal to see analytics here.</p>
        <button onClick={() => navigate("/onboarding")} className="mt-3 text-sm text-primary hover:underline">
          Set a goal →
        </button>
      </div>
    );
  }

  // rank by velocity descending
  const ranked = [...goals].sort((a, b) => (b.velocity_per_day || 0) - (a.velocity_per_day || 0));
  const leader = ranked[0];
  const hasMovement = ranked.some((g) => (g.velocity_per_day || 0) > 0);

  return (
    <div className="glass rounded-2xl p-5 sm:p-6" data-testid="multi-goal-analytics">
      <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-zinc-500 text-xs tracking-wider uppercase font-semibold">
            <ArrowRightLeft className="w-4 h-4 text-primary" /> Goal comparison
          </div>
          <p className="text-sm text-zinc-400 mt-1">
            {hasMovement ? (
              <>
                <Trophy className="inline w-3.5 h-3.5 mr-1 text-yellow-400" />
                Fastest track: <span className="text-white font-medium">{leader.role_target}</span>
                <span className="text-zinc-500"> · {leader.velocity_per_day} tasks/day</span>
              </>
            ) : "Complete some tasks to see who's leading."}
          </p>
        </div>
        <div className="text-xs text-zinc-500">{goals.length} goal{goals.length === 1 ? "" : "s"}</div>
      </div>

      <div className="space-y-4">
        {ranked.map((g, i) => {
          const isLeader = i === 0 && hasMovement;
          const isActive = g.goal_id === activeId;
          const busy = switching === g.goal_id;
          return (
            <div
              key={g.goal_id}
              data-testid={`multi-goal-row-${g.goal_id}`}
              className={`rounded-xl border p-4 transition-colors ${isActive ? "border-primary/40 bg-primary/[0.03]" : "border-white/10 hover:border-white/20"}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium truncate">{g.role_target}</div>
                    {isLeader && (
                      <span className="text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-full bg-yellow-400/15 text-yellow-300 flex items-center gap-1">
                        <Trophy className="w-3 h-3" /> Leader
                      </span>
                    )}
                    {isActive && (
                      <span className="text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-full bg-primary/15 text-primary flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Active
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {g.total_days || 0} day plan · <span className="capitalize">{g.status}</span>
                    {!g.has_roadmap && <span className="text-zinc-500"> · no roadmap yet</span>}
                  </div>
                </div>
                {!isActive && (
                  <button
                    data-testid={`multi-goal-switch-${g.goal_id}`}
                    onClick={() => activate(g)}
                    disabled={busy}
                    className="text-xs bg-primary/90 hover:bg-primary text-black px-3 py-1.5 rounded-lg font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRightLeft className="w-3.5 h-3.5" />}
                    Switch
                  </button>
                )}
              </div>

              {/* dual bars */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ProgressBar label="Readiness" value={g.readiness_score} max={100} color="bg-primary" testid={`bar-readiness-${g.goal_id}`} />
                <ProgressBar label="Completion" value={g.completion_rate} max={100} color="bg-emerald-500" testid={`bar-completion-${g.goal_id}`} />
              </div>

              <div className="mt-3 grid grid-cols-3 gap-3 text-center">
                <MiniStat icon={Flame} label="Streak" value={g.streak_count} suffix="d" iconClass="text-accent" />
                <MiniStat icon={Trophy} label="Best" value={g.best_streak} suffix="d" iconClass="text-yellow-400" />
                <MiniStat icon={Zap} label="Velocity" value={g.velocity_per_day} suffix="/d" iconClass="text-primary" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProgressBar({ label, value, max, color, testid }) {
  const pct = Math.max(0, Math.min(100, Math.round((value / max) * 100)));
  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-[11px] mb-1">
        <span className="text-zinc-500 tracking-wider uppercase font-semibold">{label}</span>
        <span className="text-zinc-300 font-medium">{value}/{max}</span>
      </div>
      <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, suffix, iconClass }) {
  return (
    <div className="rounded-lg bg-white/[0.03] border border-white/5 py-2">
      <div className={`flex items-center justify-center gap-1 text-[10px] tracking-wider uppercase font-semibold text-zinc-500 ${iconClass || ""}`}>
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-lg font-heading font-extrabold mt-0.5">{value}<span className="text-xs text-zinc-500 ml-0.5">{suffix}</span></div>
    </div>
  );
}
